import { i18n, Messages } from '@lingui/core';

const supportedLocales = ['en', 'nb', 'fi', 'da', 'sv'] as const;
type SupportedLocale = (typeof supportedLocales)[number];

const defaultLocale = 'en';

const getSupportedLocale = (usedLocale: string) =>
    supportedLocales.find(
        (locale) => usedLocale === locale || usedLocale.toLowerCase().includes(locale),
    ) || defaultLocale;

function detectLocale(): SupportedLocale {
    if (typeof window === 'undefined') {
        /**
         * Server locale detection. This requires e.g LANG environment variable to be set on the server.
         */
        const serverLocale =
            process.env.NMP_LANGUAGE || Intl.DateTimeFormat().resolvedOptions().locale;
        return getSupportedLocale(serverLocale);
    }

    try {
        /**
         * Client locale detection. Expects the lang attribute to be defined.
         */
        const htmlLocale = document.documentElement.lang;
        return getSupportedLocale(htmlLocale);
    } catch (e) {
        console.warn('could not detect locale, falling back to source locale', e);
        return defaultLocale;
    }
}

const getMessages = (
    locale: SupportedLocale,
    enMsg: Messages,
    nbMsg: Messages,
    fiMsg: Messages,
    daMsg: Messages,
    svMsg: Messages,
) => {
    if (locale === 'nb') return nbMsg;
    if (locale === 'fi') return fiMsg;
    if (locale === 'da') return daMsg;
    if (locale === 'sv') return svMsg;
    // Default to English
    return enMsg;
};

const loadedLocales = new Set<SupportedLocale>();

export const activateLang = ({
    en,
    nb,
    fi,
    da,
    sv
}: {
    en: Messages;
    nb: Messages;
    fi: Messages;
    da: Messages;
    sv: Messages;
}) => {
    const locale = detectLocale();

    // Steady state on re-render: the locale's messages are already loaded and the
    // locale is already active, so there is nothing to do. Skipping here is important:
    // both i18n.load() and i18n.activate() emit a "change" event, and components
    // (e.g. ProgressBar/Spinner) call activateLang() during render. Emitting "change"
    // while rendering triggers I18nProvider's setState mid-render — React's
    // "Cannot update a component while rendering a different component" warning —
    // and, with a reactive useLingui() subscriber in the tree, an infinite render loop.
    if (loadedLocales.has(locale) && i18n.locale === locale) {
        return;
    }

    if (!loadedLocales.has(locale)) {
        const messages = getMessages(locale, en, nb, fi, da, sv);
        i18n.load(locale, messages);
        loadedLocales.add(locale);
    }

    // Only activate when the locale actually differs — activate() also emits "change".
    if (i18n.locale !== locale) {
        i18n.activate(locale);
    }
};
